import { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useSubcollection, useSubcollectionMutations } from '../../hooks/useFirestore'
import './CharacterNotes.css'

// Format timestamp to readable date
function formatTimestamp(timestamp) {
  if (!timestamp) return ''
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

export default function CharacterNotes({ characterId, linkedUserId }) {
  const { user, isOwner } = useAuth()
  const { data: notes, loading } = useSubcollection('characters', characterId, 'notes', {
    orderByField: 'createdAt',
    orderDirection: 'desc'
  })
  const { add, update, remove } = useSubcollectionMutations('characters', characterId, 'notes')

  const [newNote, setNewNote] = useState('')
  const [newNoteHidden, setNewNoteHidden] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editContent, setEditContent] = useState('')
  const [editHidden, setEditHidden] = useState(false)
  const [saving, setSaving] = useState(false)

  // Filter notes based on visibility permissions
  const visibleNotes = notes.filter(note => {
    // Always show public notes
    if (!note.hidden) return true
    // Show hidden notes to admin
    if (isOwner) return true
    // Show hidden notes to the note author
    if (user && note.userId === user.uid) return true
    // Show hidden notes to linked user (character owner)
    if (user && linkedUserId === user.uid) return true
    return false
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!newNote.trim() || !user) return

    setSaving(true)
    try {
      await add({
        userId: user.uid,
        userName: user.displayName || user.email,
        content: newNote.trim(),
        hidden: newNoteHidden
      })
      setNewNote('')
      setNewNoteHidden(false)
    } catch (err) {
      console.error('Failed to add note:', err)
    }
    setSaving(false)
  }

  const handleEdit = (note) => {
    setEditingId(note.id)
    setEditContent(note.content)
    setEditHidden(note.hidden || false)
  }

  const handleSaveEdit = async () => {
    if (!editContent.trim()) return

    setSaving(true)
    try {
      await update(editingId, {
        content: editContent.trim(),
        hidden: editHidden
      })
      setEditingId(null)
      setEditContent('')
      setEditHidden(false)
    } catch (err) {
      console.error('Failed to update note:', err)
    }
    setSaving(false)
  }

  const handleDelete = async (noteId) => {
    if (!window.confirm('Delete this note?')) return

    try {
      await remove(noteId)
    } catch (err) {
      console.error('Failed to delete note:', err)
    }
  }

  const canEditNote = (note) => {
    if (!user) return false
    if (isOwner) return true
    return note.userId === user.uid
  }

  if (loading) {
    return (
      <section className="character-notes">
        <h2>Notes</h2>
        <div className="notes-loading">Loading notes...</div>
      </section>
    )
  }

  return (
    <section className="character-notes">
      <h2>Notes</h2>

      {visibleNotes.length === 0 ? (
        <p className="notes-empty">No notes yet. Be the first to add one!</p>
      ) : (
        <div className="notes-list">
          {visibleNotes.map(note => (
            <div key={note.id} className={`note-item ${note.hidden ? 'hidden-note' : ''}`}>
              {editingId === note.id ? (
                <div className="note-edit">
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={3}
                    autoFocus
                  />
                  <label className="note-hidden-toggle">
                    <input
                      type="checkbox"
                      checked={editHidden}
                      onChange={(e) => setEditHidden(e.target.checked)}
                    />
                    <span>Hidden (only visible to you, admin, and character owner)</span>
                  </label>
                  <div className="note-edit-actions">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleSaveEdit}
                      disabled={saving}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setEditingId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="note-header">
                    <span className="note-author">{note.userName}</span>
                    <span className="note-date">{formatTimestamp(note.createdAt)}</span>
                    {note.hidden && <span className="note-badge hidden">Hidden</span>}
                  </div>
                  <div className="note-content">{note.content}</div>
                  {canEditNote(note) && (
                    <div className="note-actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleEdit(note)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleDelete(note.id)}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {user ? (
        <form className="note-form" onSubmit={handleSubmit}>
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Add a note about this character..."
            rows={3}
          />
          <div className="note-form-footer">
            <label className="note-hidden-toggle">
              <input
                type="checkbox"
                checked={newNoteHidden}
                onChange={(e) => setNewNoteHidden(e.target.checked)}
              />
              <span>Hidden</span>
            </label>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={saving || !newNote.trim()}
            >
              {saving ? 'Posting...' : 'Post Note'}
            </button>
          </div>
        </form>
      ) : (
        <p className="notes-login-prompt">Sign in to leave a note.</p>
      )}
    </section>
  )
}
